import React, { JSX, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { convertTimestampToDateTime } from '../../../helper/helper';
import { faToggleOff, faToggleOn } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '../../../components/Common/Loader';
import TableHeader from '../../../components/Common/TableHeader';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import { getValidationSchema } from './services/plan-validation-scheme';
import { addNewCost, getUpdatedPlanDetails, savePlan } from './services/AdminForms.service';
import { initialPlanData, mapPlanDetailsToFormValues } from './services/planDetailsMapper';
import { MidjourneyModeGeneration } from '../../../interfaces/book.interface';
import { CurrencyEnum, IPlanPricing } from '../../../interfaces/plan.interface';
import MultiCurrencyPricing from './components/MultiCurrencyPricing';

interface PlanFormProps {
  setShowForm: (value: boolean) => void;
  planDetails: any;
  getPlans: (setPlans: any) => void;
  setPlans: any;
  setPlanDetails: any;
}

interface CostHistoryItem {
  stripe_plan_id?: string;
  cost?: any;
  timestamp?: any;
  isDefault?: boolean;
}

export const PlanForm = ({ setShowForm, planDetails, getPlans, setPlans, setPlanDetails }: PlanFormProps) => {
  const [loader, setLoader] = useState<boolean>(false);
  const [bookPages, setBookPages] = useState<Array<JSX.Element>>([]);
  const [costHistory, setCostHistory] = useState<CostHistoryItem[]>([]);
  const [newCostLoader, setNewCostLoader] = useState<boolean>(false);
  const [pricing, setPricing] = useState<IPlanPricing[]>([]);

  const formik = useFormik({
    initialValues: initialPlanData,
    validationSchema: getValidationSchema(planDetails),
    enableReinitialize: true,
    onSubmit: (values) => {
      handleSubmit();
    },
  });

  const bookPageList = () => {
    let newOptions: Array<JSX.Element> = [];

    for (let i = 16; i <= 52; i = i + 4) {
      newOptions.push(
        <option key={i} value={i}>
          {i}
        </option>,
      );
    }
    setBookPages(newOptions);
  };

  const handleSubmit = async () => {
    await savePlan(planDetails, formik.values, getPlans, setPlans, setLoader, setShowForm, pricing);
  };

  const setDefaultPricesList = (): void => {
    setPricing([
      {
        currency: CurrencyEnum.USD,
        cost: 0,
        stripe_plan_id: '',
        isDefault: true,
      },
      {
        currency: CurrencyEnum.EUR,
        cost: 0,
        stripe_plan_id: '',
        isDefault: false,
      },
      {
        currency: CurrencyEnum.GBP,
        cost: 0,
        stripe_plan_id: '',
        isDefault: false,
      },
    ]);
  };

  useEffect(() => {
    if (planDetails) {
      const values = mapPlanDetailsToFormValues(planDetails);
      formik.setValues(values);

      // Initialize pricing from plan details
      if (planDetails?.pricing && planDetails.pricing.length > 0) {
        setPricing(planDetails.pricing);
      } else {
        // Fallback to legacy single-currency pricing
        setPricing([
          {
            currency: CurrencyEnum.USD,
            cost: parseFloat(planDetails?.cost || '0'),
            stripe_plan_id: planDetails?.stripe_plan_id || '',
            isDefault: true,
          },
        ]);
      }

      if (planDetails?.costHistory) {
        setCostHistory(planDetails?.costHistory);
      } else {
        setCostHistory([
          {
            stripe_plan_id: planDetails?.stripe_plan_id,
            cost: planDetails?.cost,
            timestamp: planDetails?.timestamp,
          },
        ]);
      }
    } else {
      // Initialize with default pricing for new plans
      setDefaultPricesList();
    }
  }, [planDetails]);

  useEffect(() => {
    bookPageList();
  }, []);

  const handleAddNewCost = async () => {
    await addNewCost(
      planDetails,
      formik.values?.cost,
      costHistory,
      setCostHistory,
      setNewCostLoader,
      setPlanDetails,
      getUpdatedPlanDetails,
    );
  };

  return (
    <div className="bg-storytan mx-auto">
      <div className="max-w-7xl mx-auto bg-storytan ">
        {planDetails ? (
          <h1 className="text-storyblue text-xl font-bold text-start mb-4">Edit Plan</h1>
        ) : (
          <h1 className="text-storyblue text-xl font-bold text-start mb-4">Create New Plan</h1>
        )}
        <form
          id="form-admin-plans"
          name="form-admin-plans"
          className="text-start bg-storytan"
          onSubmit={formik.handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-x-2 md:gap-y-[.25rem]">
            <div className="flex flex-col gap-2">
              <label className="font-medium">Plan Name</label>
              <input
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder="Plan Name"
                className="p-2 w-full rounded-md"
                autoFocus={true}
              />
              <div className="text-red-600 mt-1">{formik.errors.name}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium">Charging Cycle</label>
              <select
                name="chargeCycle"
                value={formik.values.chargeCycle}
                onChange={formik.handleChange}
                className="p-2 w-full rounded-md"
                disabled={planDetails}
              >
                <option value="">Select Cycle</option>
                <option value={'one_time'}>One Time</option>
                <option value={'month'}>Monthly Recurring</option>
                <option value={'year'}>Yearly Recurring</option>
              </select>
              <div className="text-red-600 mt-1">{formik.errors.chargeCycle}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="storyCreationLimit">
                Story Creation Limit
              </label>
              <input
                type="number"
                id="storyCreationLimit"
                name="storyCreationLimit"
                value={formik.values.storyCreationLimit}
                onChange={formik.handleChange}
                placeholder="Story Creation Limit"
                className={`p-2 w-full rounded-md disabled:bg-gray-200 disabled:text-gray-500
                  "bg-white "
                `}
                disabled={formik.values.freePhysicalBookStatus === true}
              />
              <div className="text-red-600 mt-1">{formik.errors.storyCreationLimit}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="storyCreationLimit">
                Image Credits Per Book
              </label>
              <input
                type="number"
                id="imageCredits"
                name="imageCredits"
                min={1}
                value={formik.values.imageCredits}
                onChange={formik.handleChange}
                placeholder="Image Credits Per Book"
                className={`p-2 w-full rounded-md ${planDetails?.imageCredits ? 'bg-white text-[#9EA4B0]' : ''}`}
              />
              <div className="text-red-600 mt-1">{formik.errors.imageCredits}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="storyCreationLimit">
                Additional Image Credits Per Book
              </label>
              <input
                type="number"
                id="additionalImageCredits"
                name="additionalImageCredits"
                min={1}
                value={formik.values.additionalImageCredits}
                onChange={formik.handleChange}
                placeholder="Additional Image Credits Per Book"
                className={`p-2 w-full rounded-md ${
                  planDetails?.additionalImageCredits ? 'bg-white text-[#9EA4B0]' : ''
                }`}
              />
              <div className="text-red-600 mt-1">{formik.errors.additionalImageCredits}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="storyCreationLimit">
                Additional Image Credits Cost Per Book ($)
              </label>
              <input
                type="text"
                id="additionalImageCreditsCost"
                name="additionalImageCreditsCost"
                min={1}
                value={formik.values.additionalImageCreditsCost}
                onChange={formik.handleChange}
                placeholder="Additional Image Credits Cost Per Book"
                className={`p-2 w-full rounded-md ${
                  planDetails?.additionalImageCreditsCost ? 'bg-white text-[#9EA4B0]' : ''
                }`}
              />
              <div className="text-red-600 mt-1">{formik.errors.additionalImageCreditsCost}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="storyCreationLimit">
                Additional Story Creation Limit
              </label>
              <input
                type="number"
                id="additionalStoryCreationLimit"
                name="additionalStoryCreationLimit"
                min={1}
                value={formik.values.additionalStoryCreationLimit}
                onChange={formik.handleChange}
                placeholder="Additional Story Creation Limit"
                className={`p-2 w-full rounded-md ${
                  planDetails?.additionalStoryCreationLimit ? 'bg-white text-[#9EA4B0]' : ''
                }`}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="storyCreationLimit">
                Additional Story Creation Cost ($)
              </label>
              <input
                type="text"
                id="additionalStoryCreationCost"
                name="additionalStoryCreationCost"
                min={1}
                value={formik.values.additionalStoryCreationCost}
                onChange={formik.handleChange}
                placeholder="Additional Story Creation Cost"
                className={`p-2 w-full rounded-md ${
                  planDetails?.additionalStoryCreationCost ? 'bg-white text-[#9EA4B0]' : ''
                }`}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="storyCreationLimit">
                Maximum Book Pages
              </label>
              <select
                id="maxBookPage"
                name="maxBookPage"
                className="row-start-1 col-start-1 w-full rounded-md text-sm md:text-xl p-2 appearance-none"
                value={formik.values.maxBookPage}
                onChange={formik.handleChange}
              >
                <option value="" disabled>
                  Please Select Page Total
                </option>
                {bookPages}
              </select>

              <div className="text-red-600 mt-1">{formik.errors.maxBookPage}</div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="modeCharacterGeneration">
                Character Generation Mode
              </label>
              <select
                id="modeCharacterGeneration"
                name="modeCharacterGeneration"
                className="row-start-1 col-start-1 w-full rounded-md text-sm md:text-xl p-2 appearance-none"
                value={formik.values.modeCharacterGeneration}
                onChange={formik.handleChange}
              >
                <option value="">Please Select Mode</option>
                <option value={MidjourneyModeGeneration.fast}>Fast</option>
                <option value={MidjourneyModeGeneration.relaxed}>Relaxed</option>
              </select>

              <div className="text-red-600 mt-1">{formik.errors.modeCharacterGeneration}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="modeIllustrationGeneration">
                Illustration Generation Mode
              </label>
              <select
                id="modeIllustrationGeneration"
                name="modeIllustrationGeneration"
                className="row-start-1 col-start-1 w-full rounded-md text-sm md:text-xl p-2 appearance-none"
                value={formik.values.modeIllustrationGeneration}
                onChange={formik.handleChange}
              >
                <option value="">Please Select Mode</option>
                <option value={MidjourneyModeGeneration.fast}>Fast</option>
                <option value={MidjourneyModeGeneration.relaxed}>Relaxed</option>
              </select>

              <div className="text-red-600 mt-1">{formik.errors.modeIllustrationGeneration}</div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-medium">{planDetails ? 'New Price ($)' : 'Price ($)'}</label>
              <input
                type="number"
                name="cost"
                value={formik.values.cost}
                onChange={formik.handleChange}
                placeholder={planDetails ? 'New Price' : 'Price'}
                className="p-2 w-full rounded-md"
              />
              <div className="text-red-600 mt-1">{formik.errors.cost}</div>
            </div>

            {/* Multi-Currency Pricing Section */}
            <div className="col-span-1 md:col-span-2">
              <MultiCurrencyPricing pricing={pricing} onChange={setPricing} isEditMode={!!planDetails} />
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-medium">Trial Days</label>
              <input
                type="number"
                name="trialDays"
                value={formik.values.trialDays}
                onChange={formik.handleChange}
                placeholder="Trial Peroid"
                className="p-2 w-full rounded-md bg-white disabled:bg-gray-200 disabled:text-gray-500"
                disabled={!!planDetails}
              />
              <div className="text-red-600 mt-1">{formik.errors.trialDays}</div>
            </div>
            <div className="flex flex-row gap-2 justify-between ">
              <div className="flex flex-col gap-2  ml-2  ">
                <label className="font-medium">Maintainace Mode: </label>
                <FontAwesomeIcon
                  icon={formik.values.maintenanceMode ? faToggleOn : faToggleOff}
                  className={`h-8 w-8 cursor-pointer ${
                    formik.values.maintenanceMode ? 'text-storygreen' : 'text-red-500'
                  } `}
                  title={formik.values.maintenanceMode ? 'Deactivate ' : 'Activate '}
                  onClick={() => {
                    formik.setFieldValue('maintenanceMode', !formik.values.maintenanceMode);
                  }}
                />
              </div>

              <div className="flex flex-col gap-2  ml-2">
                <label className="font-medium"> Free Physical Book: </label>
                <FontAwesomeIcon
                  icon={formik.values.freePhysicalBookStatus ? faToggleOn : faToggleOff}
                  className={`h-8 w-8 cursor-pointer ${
                    formik.values.freePhysicalBookStatus ? 'text-storygreen' : 'text-red-500'
                  } `}
                  title={formik.values.freePhysicalBookStatus ? 'Deactivate ' : 'Activate '}
                  onClick={() => {
                    formik.setFieldValue('freePhysicalBookStatus', !formik.values.freePhysicalBookStatus);
                  }}
                />
              </div>
              <div className="flex flex-col gap-2 mr-2">
                <label className="font-medium">Free Books Quantity</label>
                <input
                  type="number"
                  name="freePhysicalBookQuantity"
                  value={formik.values.freePhysicalBookQuantity}
                  onChange={formik.handleChange}
                  placeholder="Free Books Quantity"
                  className="p-2 w-full rounded-md bg-white disabled:bg-gray-200 disabled:text-gray-500"
                  disabled={formik.values.freePhysicalBookStatus === false}
                />
                <div className="text-red-600 mt-1">{formik.errors.freePhysicalBookQuantity}</div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-medium" htmlFor="mics">
                Misc Details
              </label>
              <textarea
                id="mics"
                name="mics"
                minLength={3}
                value={formik.values.mics}
                onChange={formik.handleChange}
                placeholder="Enter details to be displayed to user..."
                className="p-2 w-full rounded-md bg-white"
              />
            </div>
            <div className="flex flex-row gap-2 justify-between ">
              <div className="flex flex-col gap-2  ml-2  ">
                <label className="font-medium">Show in Subscription popup: </label>
                <FontAwesomeIcon
                  icon={formik.values.showOnSubscription ? faToggleOn : faToggleOff}
                  className={`h-8 w-8 cursor-pointer ${
                    formik.values.showOnSubscription ? 'text-storygreen' : 'text-red-500'
                  } `}
                  title={formik.values.showOnSubscription ? 'Deactivate ' : 'Activate '}
                  onClick={() => {
                    formik.setFieldValue('showOnSubscription', !formik.values.showOnSubscription);
                  }}
                />
              </div>
            </div>

            <div></div>
            {planDetails && (
              <button
                type="button"
                onClick={() => {
                  handleAddNewCost();
                }}
                disabled={!formik?.values?.cost || formik?.values?.cost?.length < 1}
                className={` ${
                  formik?.values?.cost?.length < 1 || newCostLoader ? 'opacity-[50%]' : ''
                } mt-8 flex justify-center gap-2 bg-storyblue text-white font-bold rounded-md text-base h-10 items-center w-52`}
              >
                {planDetails ? 'Add New Cost' : ' Add Cost'} <Loader loader={newCostLoader} />
              </button>
            )}
          </div>

          {planDetails && costHistory && (
            <div>
              <h3 className=" my-2 font-bold">Plan Price History </h3>
              <div className=" h-[135px] overflow-y-scroll">
                <table className="min-w-full border border-gray-300 ">
                  {TableHeader(['Price', 'Created At'])}

                  <tbody>
                    {costHistory && costHistory.length > 0 ? (
                      costHistory?.map((history, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                          <td className="text-left py-2 px-4 border-b">
                            {'$ ' + history?.cost || '-'}
                            <span className="border-b ml-2 text-sm text-storygreen italic">
                              {history?.isDefault === true ? 'Default' : ''}
                            </span>
                          </td>
                          <td className="text-left py-2 px-4 border-b">
                            {convertTimestampToDateTime(history?.timestamp) || '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <NoRecordFound colSpan={4} />
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3 flex-wrap md:flex-nowrap">
            <button
              type="submit"
              className={`${loader ? 'opacity-[50%]' : ''}  mt-2 md:mt-[1rem] flex justify-center gap-2 bg-storyblue text-white font-bold rounded-md text-base items-center`}
              disabled={loader}
            >
              Save
              <Loader loader={loader} />
            </button>

            <button
              type="button"
              className={`mt-2 md:mt-[1rem] flex justify-center bg-storyblueSecondLightText text-white hover:text-white font-bold rounded-md text-base p-2`}
              onClick={() => setShowForm(false)}
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
